import { useEffect, useRef, useCallback } from 'react';
import { useTrackingApiService } from '../api/providers/AppServiceProvider';
import { DeviceIdHelper } from '../common/helpers/device-id-helper';
import { useMsal } from '@azure/msal-react';

export const usePageTracking = (eventName: string, menuName: string, eventTimeMs?: number, displayName?: string) => {
	const trackingService = useTrackingApiService();
	const { accounts } = useMsal();
	const hasTracked = useRef<boolean>(false);
	const deviceId = useRef<string>(DeviceIdHelper.getOrCreateDeviceId());

	useEffect(() => {
		if (hasTracked.current) {
			return;
		}

		if (accounts.length === 0) {
			return;
		}

		const pageUrl = window.location.href;

		trackingService.TrackPageView(eventName, pageUrl, menuName, displayName, eventTimeMs, deviceId.current)
			.then(() => {
				hasTracked.current = true;
			})
			.catch((error) => {
				console.error('Failed to track page view:', error);
			});
	}, [eventName, menuName, eventTimeMs, displayName, trackingService, accounts.length]);
};

export const usePowerBITracking = (menuName: string) => {
	const trackingService = useTrackingApiService();
	const { accounts } = useMsal();
	const lastTrackedPage = useRef<string | null>(null);
	const deviceId = useRef<string>(DeviceIdHelper.getOrCreateDeviceId());

	const trackPowerBIPage = (pageId: string, pageDisplayName?: string) => {
		if (accounts.length === 0) {
			return;
		}

		if (lastTrackedPage.current === pageId) {
			return;
		}

		const pageUrl = window.location.href;

		trackingService.TrackPageView('Page Viewed', pageUrl, menuName, pageDisplayName, undefined, deviceId.current)
			.then(() => {
				lastTrackedPage.current = pageId;
			})
			.catch((error) => {
				console.error('Failed to track page view:', error);
			});
	};

	const trackPowerBIEvent = useCallback((eventName: string, displayName?: string, visualName?: string, visualType?: string, filterData?: string, selectionData?: string) => {
		if (accounts.length === 0) {
			return;
		}

		if (!trackingService) {
			console.error('Tracking service is not available');
			return;
		}

		const pageUrl = window.location.href;

		if ('TrackPowerBIEvent' in trackingService && typeof (trackingService as any).TrackPowerBIEvent === 'function') {
			try {
				(trackingService as any).TrackPowerBIEvent(eventName, pageUrl, menuName, displayName, visualName, visualType, filterData, selectionData, undefined, deviceId.current)
					.catch((error: any) => {
						console.error('Failed to track event:', error);
					});
			} catch (error) {
				console.error('Error calling TrackPowerBIEvent:', error);
			}
		} else {
			console.warn('TrackPowerBIEvent method not available. Using TrackPageView as fallback.');
			trackingService.TrackPageView(eventName, pageUrl, menuName, displayName, undefined, deviceId.current)
				.catch((error) => {
					console.error('Failed to track event (fallback):', error);
				});
		}
	}, [accounts.length, trackingService, menuName]);

	return { trackPowerBIPage, trackPowerBIEvent };
};

